import { useCallback } from 'react';
import { getLanguageService } from '../service/ServiceRegistry';
import type { FetchDataFn, TableQueryParams, TableDataResponse } from '../types/table';
import type { Language, LanguagesQueryParams, CreateLanguageRequest, UpdateLanguageRequest } from '../types/api/language';

/**
 * Custom hook for language management
 * 
 * Integrates LanguageService with the DataTable component
 * Handles data transformation between API and table formats
 * 
 * Features:
 * - Automatic data fetching
 * - Filter handling
 * - Pagination conversion (DataTable uses 1-based, API uses 0-based)
 * - Error handling
 * - Type safety
 */
export function useLanguages() {
  const languageService = getLanguageService();

  /**
   * Fetch function compatible with DataTable
   * Transforms DataTable params to API params and response back to DataTable format
   */
  const fetchLanguages: FetchDataFn<Language> = useCallback(
    async (params: TableQueryParams): Promise<TableDataResponse<Language>> => {
      try {
        // API now expects 1-based page index; pass through directly
        const apiParams: LanguagesQueryParams = {
          page: params.page,
          size: params.pageSize,
        };

        // Add search parameter if provided
        if (params.search) {
          apiParams.search = params.search;
        }

        // Fetch data from API
        const response = await languageService.getLanguages(apiParams);
        
        return {
          data: response.items,
          pagination: {
            page: response.page,
            pageSize: response.size,
            total: response.totalItems,
            totalPages: response.totalPages,
            hasNext: response.hasNext,
            hasPrev: response.hasPrevious,
          },
        };
      } catch (error) {
        console.error('Failed to fetch languages:', error);
        throw new Error('Failed to load languages. Please try again.');
      }
    },
    [languageService]
  );

  return {
    fetchLanguages,
  };
}

/**
 * Hook for language actions
 * Provides functions to perform actions on languages
 */
export function useLanguageActions() {
  const languageService = getLanguageService();

  const createLanguage = useCallback(
    async (payload: CreateLanguageRequest, iconFile: File | null): Promise<Language> => {
      try {
        return await languageService.createLanguage({ data: payload, iconFile });
      } catch (error) {
        console.error('Failed to create language:', error);
        throw error;
      }
    },
    [languageService]
  );

  const updateLanguage = useCallback(
    async (
      languageId: string,
      payload: UpdateLanguageRequest,
      iconFile: File | null
    ): Promise<Language> => {
      try {
        return await languageService.updateLanguage(languageId, { data: payload, iconFile });
      } catch (error) {
        console.error('Failed to update language:', error);
        throw error;
      }
    },
    [languageService]
  );

  const deleteLanguage = useCallback(
    async (languageId: string): Promise<void> => {
      try {
        await languageService.deleteLanguage(languageId);
      } catch (error) {
        console.error('Failed to delete language:', error);
        throw error;
      }
    },
    [languageService]
  );

  return {
    createLanguage,
    updateLanguage,
    deleteLanguage,
  };
}

